import { useMemo } from 'react';
import { ICustomQuestionTypeConfiguration } from 'survey-core';
import { usePopulateHtmlTemplate } from '../hooks/usePopulateHtmlTemplate';
import { ClientForm } from '../api/types/digitalForms.api.types';

export const useHtmlTemplateQuestions = ({ clientForm }: { clientForm?: ClientForm }) => {
  const { populateTemplate } = usePopulateHtmlTemplate();

  // Define the base HTML template
  const baseHtmlTemplate = `
    <style>
              .sd-html.sd-html .client-patient-info {
                display: grid;
                grid-template-columns: auto auto auto;
                gap: 32px;
                padding: 16px;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                background-color: #fafafa;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 16px;
                overflow: hidden;
                justify-content: start;
              }

              .sd-html.sd-html .client-patient-info .info-section {
                display: grid;
                grid-template-columns: auto 1fr;
                gap: 0px 16px;
                min-width: 0;
                align-content: start;
              }

              .sd-html.sd-html .client-patient-info .info-label {
                font-weight: bold;
                color: #333;
                white-space: nowrap;
                font-size: 14px;
              }

              .sd-html.sd-html .client-patient-info .info-value {
                color: #555;
                word-wrap: break-word;
                overflow-wrap: break-word;
                min-width: 0;
                font-size: 14px;
              }

              @media (max-width: 768px) {
                .sd-html.sd-html .client-patient-info {
                  grid-template-columns: 1fr;
                  gap: 12px;
                  padding: 12px;
                }

                .sd-html.sd-html .client-patient-info .info-section {
                  gap: 6px 12px;
                }
              }

              @media (max-width: 480px) {
                .sd-html.sd-html .client-patient-info {
                  padding: 8px;
                  gap: 8px;
                }

                .sd-html.sd-html .client-patient-info .info-section {
                  grid-template-columns: 1fr;
                  gap: 4px;
                }

                .sd-html.sd-html .client-patient-info .info-label {
                  font-size: 13px;
                  font-weight: bold;
                }

                .sd-html.sd-html .client-patient-info .info-value {
                  font-size: 13px;
                  margin-bottom: 8px;
                }
              }
            </style>
            <div class="client-patient-info">
              <div class="info-section">
                <div class="info-label">Pt ID:</div>
                <div class="info-value">{{patientId}}</div>

                <div class="info-label">Patient:</div>
                <div class="info-value">{{patientName}}</div>

                <div class="info-label">Sig:</div>
                <div class="info-value">{{patientSignalment}}</div>

                <div class="info-label">Color:</div>
                <div class="info-value">{{patientColor}}</div>

                <div class="info-label">Mchip:</div>
                <div class="info-value">{{microchipNumber}}</div>
              </div>

              <div class="info-section">
                <div class="info-label">Acc ID:</div>
                <div class="info-value">{{accountId}}</div>

                <div class="info-label">Client:</div>
                <div class="info-value">{{clientName}}</div>
              </div>
            </div>
  `;

  // HTML template question types
  const htmlTemplateQuestions: ICustomQuestionTypeConfiguration[] = useMemo(
    () => [
      {
        name: 'client_patient_info_header',
        title: 'Client & Patient Information Header',
        questionJSON: {
          type: 'html',
          titleLocation: 'hidden',
          html: clientForm ? populateTemplate({ template: baseHtmlTemplate, clientForm }) : baseHtmlTemplate,
        },
        inheritBaseProps: true,
      },
    ],
    [clientForm, populateTemplate]
  );

  return { htmlTemplateQuestions };
};
